import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Button, Select, Card, DataTable, Modal } from '../../components/ui';
import ScadenzeCollegateModal, {
  caricaScadenzeCollegate,
  riassuntoScadenze
} from '../../components/ScadenzeCollegateModal';

function AssociazioniPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  // Data state
  const [fornitori, setFornitori] = useState([]);
  const [rsaList, setRsaList] = useState([]);
  const [tipologieIntervento, setTipologieIntervento] = useState([]);
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selected items
  const [selectedFornitore, setSelectedFornitore] = useState(null);
  const [fornitoreRelations, setFornitoreRelations] = useState({
    tipologie: [],
    rsa: [],
    contratti: []
  });

  // Modal states
  const [tipologieModalOpen, setTipologieModalOpen] = useState(false);
  const [rsaModalOpen, setRsaModalOpen] = useState(false);
  const [selectedTipologie, setSelectedTipologie] = useState([]);
  const [selectedRsa, setSelectedRsa] = useState([]);
  const [savingTipologie, setSavingTipologie] = useState(false);
  const [savingRsa, setSavingRsa] = useState(false);
  // Scadenze aperte delle tipologie che si stanno scollegando: { scadenze, puoEliminare }
  const [scadenzeCollegate, setScadenzeCollegate] = useState(null);

  // Load all data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fornitoriData, rsaData, tipologieData, contrattiData] = await Promise.all([
        apiRequest('/fornitori'),
        apiRequest('/rsa'),
        apiRequest('/tipologie-intervento'),
        apiRequest('/contratti')
      ]);

      setFornitori(fornitoriData.value || []);
      setRsaList(rsaData.value || []);
      setTipologieIntervento(tipologieData.value || []);
      setContratti(contrattiData.value || []);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Errore nel caricamento dei dati', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load fornitore relationships
  const loadFornitoreRelations = async (fornitoreId) => {
    try {
      const [tipologieData, rsaData, contrattiData] = await Promise.all([
        apiRequest(`/fornitori/${fornitoreId}/tipologie-intervento`).catch(() => ({ value: [] })),
        apiRequest(`/fornitori/${fornitoreId}/rsa`).catch(() => ({ value: [] })),
        apiRequest(`/contratti?fornitoreId=${fornitoreId}`)
      ]);

      setFornitoreRelations({
        tipologie: tipologieData.value || [],
        rsa: rsaData.value || [],
        contratti: contrattiData.value || []
      });

      setSelectedTipologie(tipologieData.value?.map(t => t.id) || []);
      setSelectedRsa(rsaData.value?.map(r => r.id) || []);
    } catch (error) {
      console.error('Error loading fornitore relations:', error);
      setFornitoreRelations({ tipologie: [], rsa: [], contratti: [] });
    }
  };

  // Handle fornitore selection
  const handleFornitoreSelect = (fornitoreId) => {
    const fornitore = fornitori.find(f => f.id === fornitoreId);
    setSelectedFornitore(fornitore);
    if (fornitore) {
      loadFornitoreRelations(fornitoreId);
    } else {
      setFornitoreRelations({ tipologie: [], rsa: [], contratti: [] });
    }
  };

  // Toggle tipologia selection
  const handleTipologiaToggle = (tipologiaId) => {
    setSelectedTipologie(prev => {
      if (prev.includes(tipologiaId)) {
        return prev.filter(id => id !== tipologiaId);
      }
      return [...prev, tipologiaId];
    });
  };

  // Toggle RSA selection
  const handleRsaToggle = (rsaId) => {
    setSelectedRsa(prev => {
      if (prev.includes(rsaId)) {
        return prev.filter(id => id !== rsaId);
      }
      return [...prev, rsaId];
    });
  };

  // Save tipologie associations. Senza sceltaScadenze, se le tipologie tolte hanno
  // scadenze aperte del fornitore, si chiede prima cosa farne.
  const saveTipologieAssociations = async (sceltaScadenze = null) => {
    if (!selectedFornitore) return;

    setSavingTipologie(true);
    try {
      if (!sceltaScadenze) {
        const collegate = await caricaScadenzeCollegate(
          apiRequest,
          selectedFornitore.id,
          fornitoreRelations.tipologie.map(t => t.id),
          selectedTipologie
        );
        if (collegate) {
          setScadenzeCollegate(collegate);
          return;
        }
      }

      const result = await apiRequest(`/fornitori/${selectedFornitore.id}/tipologie-intervento`, {
        method: 'PUT',
        body: JSON.stringify({
          tipologieIds: selectedTipologie,
          scadenzeCollegate: sceltaScadenze || 'lascia'
        })
      });

      const scadenzeMsg = riassuntoScadenze(result);
      if (result.warning) {
        showToast(result.message, 'warning');
      } else if (result.scadenzeError || result.scadenze?.nonEliminate.length > 0) {
        showToast(`Tipologie aggiornate: ${scadenzeMsg}`, 'warning');
      } else {
        showToast(
          scadenzeMsg ? `Tipologie aggiornate: ${scadenzeMsg}` : 'Tipologie aggiornate con successo',
          'success'
        );
      }
      setScadenzeCollegate(null);
      setTipologieModalOpen(false);
      loadFornitoreRelations(selectedFornitore.id);
    } catch (error) {
      console.error('Error saving tipologie:', error);
      showToast(error.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setSavingTipologie(false);
    }
  };

  // Save RSA associations
  const saveRsaAssociations = async () => {
    if (!selectedFornitore) return;

    setSavingRsa(true);
    try {
      const result = await apiRequest(`/fornitori/${selectedFornitore.id}/rsa`, {
        method: 'PUT',
        body: JSON.stringify({ rsaIds: selectedRsa })
      });

      if (result.warning) {
        showToast(result.message, 'warning');
      } else {
        showToast('RSA associate aggiornate con successo', 'success');
      }
      setRsaModalOpen(false);
      loadFornitoreRelations(selectedFornitore.id);
    } catch (error) {
      console.error('Error saving RSA:', error);
      showToast(error.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setSavingRsa(false);
    }
  };

  // Fornitore options for select
  const fornitoreOptions = fornitori.map(f => ({
    value: f.id,
    label: f.nome || f.phc_name
  }));

  // Group tipologie by macrocategoria
  const tipologieByMacro = tipologieIntervento.reduce((acc, tip) => {
    const macro = tip.macrocategoriaLabel || tip.macrocategoria || 'Altro';
    if (!acc[macro]) acc[macro] = [];
    acc[macro].push(tip);
    return acc;
  }, {});

  // Group RSA by regione/provincia
  const rsaByLocation = rsaList.reduce((acc, rsa) => {
    const location = rsa.provincia || rsa.phc_provincia || rsa.regione || rsa.phc_regione || 'Altro';
    if (!acc[location]) acc[location] = [];
    acc[location].push(rsa);
    return acc;
  }, {});

  // Contratti columns
  const contrattiColumns = [
    {
      key: 'nome',
      label: 'Contratto',
      render: (row) => (
        <div>
          <div className="font-medium">{row.nome || row.phc_name}</div>
          <div className="text-xs text-gray-500">{row.codiceContratto || row.phc_codicecontratto}</div>
        </div>
      )
    },
    {
      key: 'rsa',
      label: 'RSA',
      render: (row) => row.rsaNome || '-'
    },
    {
      key: 'stato',
      label: 'Stato',
      render: (row) => {
        const statoLabels = {
          100000000: 'Bozza',
          100000001: 'Attivo',
          100000002: 'In scadenza',
          100000003: 'Scaduto',
          100000004: 'Rinnovato',
          100000005: 'Annullato'
        };
        const stato = row.statoContratto || row.phc_statocontratto;
        const label = statoLabels[stato] || '-';
        const colors = {
          100000000: 'bg-gray-100 text-gray-700',
          100000001: 'bg-green-100 text-green-700',
          100000002: 'bg-yellow-100 text-yellow-700',
          100000003: 'bg-red-100 text-red-700',
          100000004: 'bg-blue-100 text-blue-700',
          100000005: 'bg-gray-100 text-gray-500'
        };
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[stato] || 'bg-gray-100'}`}>
            {label}
          </span>
        );
      }
    },
    {
      key: 'periodo',
      label: 'Periodo',
      render: (row) => {
        const inizio = row.dataDecorrenza || row.phc_datadecorrenza;
        const fine = row.dataFine || row.phc_datafine;
        return (
          <div className="text-sm">
            {inizio ? new Date(inizio).toLocaleDateString('it-IT') : '-'}
            {fine && ` → ${new Date(fine).toLocaleDateString('it-IT')}`}
          </div>
        );
      }
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <a href="/admin" className="hover:text-primary-600">Admin</a>
          <span>/</span>
          <span>Associazioni</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Gestione Associazioni</h1>
        <p className="text-gray-500 mt-1">
          Configura le relazioni tra Fornitori, RSA e Tipologie Intervento
        </p>
      </div>

      {/* Fornitore Selector */}
      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Seleziona Fornitore</h2>
        <div className="max-w-md">
          <Select
            value={selectedFornitore?.id || ''}
            onChange={(e) => handleFornitoreSelect(e.target.value)}
            options={fornitoreOptions}
            placeholder="Seleziona un fornitore per gestire le associazioni..."
          />
        </div>
      </Card>

      {selectedFornitore && (
        <div className="space-y-6">
          {/* Fornitore Info */}
          <Card className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedFornitore.nome || selectedFornitore.phc_name}
                </h2>
                <p className="text-gray-500">
                  {selectedFornitore.macrocategoriaLabel || 'Nessuna categoria'}
                </p>
                <div className="mt-2 text-sm text-gray-600">
                  <p>📧 {selectedFornitore.emailPrincipale || selectedFornitore.phc_emailprincipale || '-'}</p>
                  <p>📞 {selectedFornitore.telefono || selectedFornitore.phc_telefono || '-'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href={`/admin/fornitori/${selectedFornitore.id}`}
                  className="btn-secondary text-sm"
                >
                  Modifica Fornitore
                </a>
              </div>
            </div>
          </Card>

          {/* RSA Associate Direttamente */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">RSA Associate</h3>
                <p className="text-sm text-gray-500">
                  RSA in cui questo fornitore può operare (associazione diretta)
                </p>
              </div>
              <Button variant="primary" onClick={() => setRsaModalOpen(true)}>
                Modifica RSA
              </Button>
            </div>

            {fornitoreRelations.rsa.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {fornitoreRelations.rsa.map((rsa, idx) => (
                  <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="font-medium text-gray-900">{rsa.nome || rsa.phc_name}</div>
                    <div className="text-sm text-gray-500">{rsa.comune || rsa.phc_comune}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Nessuna RSA associata direttamente</p>
                <Button
                  variant="secondary"
                  className="mt-4"
                  onClick={() => setRsaModalOpen(true)}
                >
                  Associa RSA
                </Button>
              </div>
            )}
          </Card>

          {/* Tipologie Intervento Section */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Tipologie Intervento Abilitate</h3>
                <p className="text-sm text-gray-500">
                  Interventi che questo fornitore può eseguire
                </p>
              </div>
              <Button variant="primary" onClick={() => setTipologieModalOpen(true)}>
                Modifica Tipologie
              </Button>
            </div>

            {fornitoreRelations.tipologie.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {fornitoreRelations.tipologie.map((tip, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                  >
                    {tip.nome || tip.phc_name}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Nessuna tipologia intervento associata</p>
                <Button
                  variant="secondary"
                  className="mt-4"
                  onClick={() => setTipologieModalOpen(true)}
                >
                  Aggiungi Tipologie
                </Button>
              </div>
            )}
          </Card>

          {/* Contratti Section */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Contratti con RSA</h3>
                <p className="text-sm text-gray-500">
                  Contratti attivi e passati con le RSA
                </p>
              </div>
              <a href={`/contratti/nuovo?fornitoreId=${selectedFornitore.id}`} className="btn-primary">
                + Nuovo Contratto
              </a>
            </div>

            <DataTable
              data={fornitoreRelations.contratti}
              columns={contrattiColumns}
              emptyMessage="Nessun contratto trovato per questo fornitore"
            />
          </Card>

          {/* RSA da Contratti (legacy view) */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">RSA da Contratti Attivi</h3>
            <p className="text-sm text-gray-500 mb-4">
              RSA collegate tramite contratti attivi (visione legacy)
            </p>

            {(() => {
              const rsaIds = [...new Set(fornitoreRelations.contratti
                .filter(c => c.statoContratto === 100000001 || c.phc_statocontratto === 100000001)
                .map(c => c.rsaId || c.phc_rsa))];
              const rsaServite = rsaList.filter(r => rsaIds.includes(r.id));

              return rsaServite.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rsaServite.map((rsa, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="font-medium text-gray-900">{rsa.nome || rsa.phc_name}</div>
                      <div className="text-sm text-gray-500">{rsa.comune || rsa.phc_comune}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  Nessuna RSA con contratti attivi
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      {/* RSA Modal */}
      <Modal
        isOpen={rsaModalOpen}
        onClose={() => setRsaModalOpen(false)}
        title={`RSA Associate - ${selectedFornitore?.nome || selectedFornitore?.phc_name || ''}`}
        size="xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Seleziona le RSA in cui questo fornitore può operare.
            Questa associazione è indipendente dai contratti.
          </p>

          <div className="max-h-96 overflow-y-auto space-y-4">
            {Object.entries(rsaByLocation).map(([location, rsas]) => (
              <div key={location} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{location}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {rsas.map(rsa => (
                    <label
                      key={rsa.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        selectedRsa.includes(rsa.id) ? 'bg-green-50 border border-green-200' : 'border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRsa.includes(rsa.id)}
                        onChange={() => handleRsaToggle(rsa.id)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600"
                      />
                      <div>
                        <span className="text-sm text-gray-700">{rsa.nome || rsa.phc_name}</span>
                        {(rsa.comune || rsa.phc_comune) && (
                          <span className="text-xs text-gray-500 ml-2">({rsa.comune || rsa.phc_comune})</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {selectedRsa.length} RSA selezionate
              </span>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={() => setRsaModalOpen(false)}>
                  Annulla
                </Button>
                <Button variant="primary" onClick={saveRsaAssociations} disabled={savingRsa}>
                  {savingRsa ? 'Salvataggio...' : 'Salva Associazioni'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Tipologie Modal */}
      <Modal
        isOpen={tipologieModalOpen}
        onClose={() => setTipologieModalOpen(false)}
        title={`Tipologie Intervento - ${selectedFornitore?.nome || selectedFornitore?.phc_name || ''}`}
        size="xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Seleziona le tipologie di intervento che questo fornitore può eseguire.
            Queste saranno disponibili nella selezione durante la creazione di scadenze e interventi.
          </p>

          <div className="max-h-96 overflow-y-auto space-y-4">
            {Object.entries(tipologieByMacro).map(([macro, tipologie]) => (
              <div key={macro} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{macro}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {tipologie.map(tip => (
                    <label
                      key={tip.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        selectedTipologie.includes(tip.id) ? 'bg-primary-50 border border-primary-200' : 'border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTipologie.includes(tip.id)}
                        onChange={() => handleTipologiaToggle(tip.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm text-gray-700">{tip.nome || tip.phc_name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {selectedTipologie.length} tipologie selezionate
              </span>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={() => setTipologieModalOpen(false)}>
                  Annulla
                </Button>
                <Button variant="primary" onClick={() => saveTipologieAssociations()} disabled={savingTipologie}>
                  {savingTipologie ? 'Salvataggio...' : 'Salva Associazioni'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <ScadenzeCollegateModal
        isOpen={!!scadenzeCollegate}
        scadenze={scadenzeCollegate?.scadenze}
        puoEliminare={scadenzeCollegate?.puoEliminare}
        saving={savingTipologie}
        onScelta={saveTipologieAssociations}
        onClose={() => setScadenzeCollegate(null)}
      />
    </div>
  );
}

export default AssociazioniPage;
